import React from 'react';
import { FC } from 'react';
import Distance from '../../../../styled/Distance';
import useContext from '../../../../hooks/useContext';
import { ProductContext } from '../providers/ProductProvider';
import { WrenchIcon } from '../../../../styled/Icon';
import { Column, Columns } from '../../../../styled/Columns/wrappers';
import { Caption, Head } from '../../../../styled/StockStatus/wrappers';
import useInitialRender from '../../../../hooks/useInitialRender';
import Skeleton from '../../../../components-legacy/Skeleton';
import useToggle from '../../../../hooks/useToggle';
import LinkButton from '../../../../styled/LinkButton';
import { ErrorBoundary } from 'react-error-boundary';
import Suspense from '../../../../components-legacy/Suspense';

const AssemblyInfoPanel = React.lazy(() => import('./AssemblyInfoPanel'));

enum AssemblyPriceGroup {
  contactStore = 'ContactStore',
  assemblyAvailable = 'AssemblyAvailable',
  assemblyUnavailable = 'NoAssembly',
}

const getHeader = (assemblyPriceGroup: string) => {
  switch (assemblyPriceGroup) {
    case AssemblyPriceGroup.contactStore:
      return 'Monteringstjänst vid köp i butik';
    default:
      return 'Monteringstjänst';
  }
};

const getDescription = (assemblyPriceGroup: string) => {
  switch (assemblyPriceGroup) {
    case AssemblyPriceGroup.contactStore:
      return 'Denna produkt går att få monterad, vänligen kontakta din butik för mer information.';
    case AssemblyPriceGroup.assemblyAvailable:
      return 'Denna produkt går att få monterad både vid köp i butik eller online.';
  }
};

const formatAssemblyPriceGroup = (assemblyPriceGroup: string) =>
  // Checks if assemblyPriceGroup is PG1, PG2 and so on. If that is the case
  // we format to a collective name for when assembly is available.
  assemblyPriceGroup !== AssemblyPriceGroup.contactStore &&
  assemblyPriceGroup !== AssemblyPriceGroup.assemblyUnavailable
    ? 'AssemblyAvailable'
    : assemblyPriceGroup;

export const AssemblyInfo: FC = () => {
  const [showPanel, toggleShowPanel] = useToggle(false);
  const { assemblyPriceGroup } = useContext(ProductContext);

  const formattedPriceGroup = formatAssemblyPriceGroup(assemblyPriceGroup);
  const isInitialRender = useInitialRender(true);

  const header = getHeader(formattedPriceGroup);
  const description = getDescription(formattedPriceGroup);

  const hideAssembly =
    (!header && !description) || formattedPriceGroup === AssemblyPriceGroup.assemblyUnavailable;

  if (hideAssembly) {
    return null;
  }

  if (isInitialRender()) {
    return (
      <>
        <Distance size={['3', '4']} />
        <Skeleton height="120px" />
      </>
    );
  }

  return (
    <>
      <Distance size={['3', '4']} />
      <Columns className="space-2">
        <Column>
          <WrenchIcon className="large bump-down" />
        </Column>
        <Column>
          <Head>{header}</Head>
          <Caption>{description}</Caption>

          <LinkButton weight="regular" onClick={toggleShowPanel}>
            Läs mer om montering
          </LinkButton>
        </Column>
      </Columns>

      {showPanel && (
        <Suspense>
          {/* @ts-ignore - We can resolve this when we update the dependencies/types */}
          <ErrorBoundary fallbackRender={() => null}>
            <AssemblyInfoPanel onClose={toggleShowPanel} />
          </ErrorBoundary>
        </Suspense>
      )}
    </>
  );
};
